import React, { useEffect, useState } from 'react';
import { confirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import api from '../services/api';
import type StudyModel from '../models/StudyModel';
import Table from '../components/Table';
import CreateStudyForm from '../components/CreateStudyForm';
import StudyParticipants from '../components/StudyParticipants';
import StudyAnswers from '../components/StudyAnswers';
import StudyQuestions from '../components/StudyQuestions';
import StudySlides from '../components/StudySlides';
import type DbDataModel from '../models/DbDataModel';

const Studies = () => {
    const [studies, setStudies] = useState<StudyModel[]>([]);
    const [selectedStudy, setSelectedStudy] = useState<StudyModel | undefined>(
        undefined,
    );
    const [addingStudy, setAddingStudy] = useState(false);

    const pullStudies = async () => {
        const { studies, success } = await api.getStudies();
        if (success) {
            setStudies(studies);
        }
    };

    const pullStudyDetails = async (studyId: number) => {
        const { study, success } = await api.getStudy(studyId);
        if (success) {
            setSelectedStudy(study);
        }
    };

    useEffect(() => {
        void pullStudies();
    }, []);

    const deleteStudy = async (id: number) => {
        const { success } = await api.deleteStudy(id);
        if (success) {
            setSelectedStudy(undefined);
            void pullStudies();
        }
    };

    const onStudyCreated = () => {
        setAddingStudy(false);
        void pullStudies();
    };

    const columns = ['id', 'name', 'start_date', 'length'];

    return (
        <div>
            <div className="flex items-center">
                <div className="text-primary text-3xl">Studies</div>
                <button
                    type="button"
                    className="btn btn-primary m-4"
                    onClick={async () => {
                        setAddingStudy(true);
                    }}
                >
                    Add Study
                </button>
            </div>
            <Table
                title="Studies"
                data={studies}
                columns={columns}
                onClick={(study) => {
                    void pullStudyDetails((study as DbDataModel).id);
                }}
            />

            {selectedStudy !== undefined && (
                <Dialog
                    visible={selectedStudy !== undefined}
                    header={selectedStudy.name}
                    onHide={() => {
                        setSelectedStudy(undefined);
                    }}
                >
                    <StudyParticipants study={selectedStudy} />
                    <StudyQuestions study={selectedStudy} />
                    <StudySlides study={selectedStudy} />
                    <StudyAnswers study={selectedStudy} />
                    <div className="flex mt-8">
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => {
                                confirmDialog({
                                    message: `Are you sure you want to delete ${selectedStudy.name}?`,
                                    accept: async () =>
                                        deleteStudy(selectedStudy.id),
                                });
                            }}
                        >
                            Delete Study
                        </button>
                    </div>
                </Dialog>
            )}
            {addingStudy && (
                <Dialog
                    visible={addingStudy}
                    header="Add Study"
                    onHide={() => {
                        setAddingStudy(false);
                    }}
                >
                    <CreateStudyForm onStudyCreated={onStudyCreated} />
                </Dialog>
            )}
        </div>
    );
};

export default Studies;
